/**
 * ZoneDistributionChart Component
 * 
 * Visualizes time/distance distribution across training zones.
 * Displays stacked rows for each available metric (HR, Power, Pace),
 * with a Pie Chart and detailed breakdown including progress bars.
 */

import React, { useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  LinearProgress,
  Divider,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
} from 'recharts';
import type {
  ActivityStreamResponse,
  HeartRateZone,
  PaceZone,
  ActivityZoneMetrics,
} from '../../../types';
import { aggregateZoneData, formatTime, getZoneColor } from '../../../utils/chartUtils';

interface ZoneDistributionChartProps {
  /** Activity stream data */
  streams?: ActivityStreamResponse | null;
  /** Pre-calculated zone metrics from API */
  zoneMetrics?: ActivityZoneMetrics | null;
  /** Heart rate zones configuration */
  heartRateZones?: HeartRateZone[];
  /** Pace zones configuration */
  paceZones?: PaceZone[];
  /** Whether the data is loading */
  isLoading?: boolean;
  /** Whether to show advanced metrics (Stress scores, HR comparisons) */
  showAdvancedMetrics?: boolean;
}

interface NormalizedZoneData {
  name: string;
  value: number;
  timeInZone: number;
  percentageInZone: number;
  color: string;
  rangeLabel?: string;
  [key: string]: string | number | undefined; // Add index signature for Recharts
}

interface MetricRowProps {
  title: string;
  data: NormalizedZoneData[];
  totalTime: number;
}

/**
 * Single Row Component for a Metric (Pie + List)
 */
const ZoneMetricRow: React.FC<MetricRowProps> = ({ title, data, totalTime }) => {
  if (!data || data.length === 0) return null;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <Grid container spacing={4} alignItems="center">
        {/* Left: Pie Chart */}
        <Grid item xs={12} md={4}>
          <Box sx={{ width: '100%', height: 200, position: 'relative' }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  stroke="none"
                  isAnimationActive={false}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
            {/* Center Summary */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                pointerEvents: 'none',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Total Time
              </Typography>
              <Typography variant="h6">
                {formatTime(totalTime)}
              </Typography>
            </Box>
          </Box>
        </Grid>

        {/* Right: Detailed List */}
        <Grid item xs={12} md={8}>
          <Stack spacing={1.5}>
            {data.map((zone, index) => (
              <Box key={index}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        bgcolor: zone.color,
                      }}
                    />
                    <Typography variant="body2" fontWeight={500}>
                      {zone.name}
                    </Typography>
                    {zone.rangeLabel && (
                      <Typography variant="caption" color="text.secondary">
                        ({zone.rangeLabel})
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography variant="body2" fontWeight={500}>
                      {formatTime(zone.timeInZone)}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{ flexGrow: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={zone.percentageInZone}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'action.hover',
                        '& .MuiLinearProgress-bar': {
                          bgcolor: zone.color,
                        },
                      }}
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ minWidth: 35 }}>
                    {zone.percentageInZone.toFixed(0)}%
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

const getStressColor = (score: number) => {
  if (score < 1) return '#2196f3'; // Blue
  if (score < 3) return '#4caf50'; // Green (1-2 range, covering up to 3)
  if (score < 4) return '#ffeb3b'; // Yellow
  if (score < 5) return '#ff9800'; // Orange
  return '#f44336'; // Red
};

const getStressLabel = (score: number) => {
  if (score < 1) return 'Warm Up / Recovery';
  if (score < 3) return 'Maintains Capacity';
  if (score < 4) return 'Increases Capacity (Careful)';
  if (score < 5) return 'Significantly Improves (Careful)';
  return 'Overreaching (Very Careful)';
};

const getStressDescription = (score: number) => {
  if (score < 2) return "Minor training effect. Good for recovery and maintaining base fitness.";
  if (score < 3) return "Maintains your current fitness level. Good base training.";
  if (score < 4) return "Improves your fitness. Impactful training session.";
  if (score < 5) return "Highly effective training. Substantial improvement to fitness.";
  return "Overreaching. Extremely high load, requires ample recovery.";
};

/**
 * Score Gauge Component
 */
const StressScoreGauge: React.FC<{ score: number; label: string }> = ({ score, label }) => {
  const color = getStressColor(score);
  const interpretation = getStressLabel(score);
  const description = getStressDescription(score);

  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      p: 3,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 2,
      height: '100%',
      bgcolor: 'background.paper'
    }}>
      <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={100}
          thickness={4}
          sx={{ color: (theme) => theme.palette.action.hover }}
        />
        <CircularProgress
          variant="determinate"
          value={(Math.min(score, 5) / 5) * 100}
          size={100}
          thickness={4}
          sx={{
            color: color,
            position: 'absolute',
            left: 0,
            '& .MuiCircularProgress-circle': { strokeLinecap: 'round' },
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography variant="h4" component="div" fontWeight="800" color="text.primary">
            {score.toFixed(1)}
          </Typography>
        </Box>
      </Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        {label}
      </Typography>
      <Typography variant="subtitle2" fontWeight="600" color={color} gutterBottom>
        {interpretation}
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1, minHeight: 40 }}>
        {description}
      </Typography>
    </Box>
  );
};

/**
 * ZoneDistributionChart - Visualize training zone distribution
 */
export const ZoneDistributionChart: React.FC<ZoneDistributionChartProps> = ({
  streams,
  zoneMetrics,
  heartRateZones = [],
  paceZones = [],
  isLoading = false,
  showAdvancedMetrics = false,
}) => {

  const normalizeData = (
    type: 'heartRate' | 'pace' | 'power',
    apiZones: { zone: number; time_seconds: number; percentage: number; min?: number; max?: number }[] | null | undefined,
    localZones: HeartRateZone[] | PaceZone[] | null,
    streamDataAvailable: boolean
  ): { data: NormalizedZoneData[], totalTime: number } | null => {

    // 1. Prioritize API Pre-calculated Metrics
    if (apiZones && apiZones.length > 0) {
      let totalTime = 0;
      const data = apiZones.map((z, i) => {
        totalTime += z.time_seconds;

        // Find zone definition by matching zone number from user's configured zones
        let label = '';
        if (localZones && localZones.length > 0) {
          const def = localZones.find(zone => zone.zone === z.zone);
          if (def) {
            if ('min_bpm' in def && 'max_bpm' in def) {
              label = `${def.min_bpm} - ${def.max_bpm} bpm`;
            } else if ('min_pace' in def && 'max_pace' in def) {
              label = `${def.min_pace} - ${def.max_pace}`;
            }
          }
        }

        return {
          name: `Zone ${z.zone}`,
          value: z.percentage,
          timeInZone: z.time_seconds,
          percentageInZone: z.percentage,
          color: getZoneColor(z.zone - 1), // Use zone number (1-indexed) to get color
          rangeLabel: label,
        };
      });
      return { data, totalTime };
    }

    // 2. Fallback to Stream Calculation
    if (streamDataAvailable && streams && localZones && localZones.length > 0) {
      const calculated = aggregateZoneData(streams, localZones, type);
      const data = calculated.zones.map((z, i) => ({
        name: z.name,
        value: z.percentageInZone,
        timeInZone: z.timeInZone,
        percentageInZone: z.percentageInZone,
        color: z.color,
        rangeLabel: `${z.minValue} - ${z.maxValue}`, // simplified
      }));
      return { data, totalTime: calculated.totalTime };
    }

    return null;
  };

  const heartRateData = useMemo(() =>
    normalizeData(
      'heartRate',
      zoneMetrics?.hr_zones,
      heartRateZones,
      !!streams?.heartrate
    ),
    [zoneMetrics, heartRateZones, streams]);

  const paceData = useMemo(() =>
    normalizeData(
      'pace',
      zoneMetrics?.pace_zones,
      paceZones,
      !!streams?.velocity_smooth
    ),
    [zoneMetrics, paceZones, streams]);

  const powerData = useMemo(() =>
    normalizeData(
      'power',
      zoneMetrics?.power_zones,
      null, // Power zones defs not currently passed in props
      !!streams?.watts
    ),
    [zoneMetrics, streams]);


  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!heartRateData && !paceData && !powerData && !showAdvancedMetrics) {
    return (
      <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
        No zone data available.
      </Typography>
    );
  }

  return (
    <Box>


      {heartRateData && (
        <>
          <ZoneMetricRow title="Heart Rate Zones" data={heartRateData.data} totalTime={heartRateData.totalTime} />
          {(paceData || powerData) && <Divider sx={{ my: 4 }} />}
        </>
      )}

      {paceData && (
        <>
          <ZoneMetricRow title="Pace Zones" data={paceData.data} totalTime={paceData.totalTime} />
          {powerData && <Divider sx={{ my: 4 }} />}
        </>
      )}

      {powerData && (
        <ZoneMetricRow title="Power Zones" data={powerData.data} totalTime={powerData.totalTime} />
      )}
      {/* Advanced Physiology Metrics - Moved to Bottom */}
      {showAdvancedMetrics && (
        <>
          {(heartRateData || paceData || powerData) && <Divider sx={{ my: 4 }} />}
          {zoneMetrics && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
                Physiological Impact
              </Typography>

              <Grid container spacing={3} sx={{ mb: 4 }}>
                {/* Aerobic Stress */}
                {zoneMetrics.aerobic_training_stress !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <StressScoreGauge score={zoneMetrics.aerobic_training_stress} label="Aerobic Effect" />
                  </Grid>
                )}

                {/* Anaerobic Stress */}
                {zoneMetrics.anaerobic_training_stress !== undefined && (
                  <Grid item xs={12} sm={6}>
                    <StressScoreGauge score={zoneMetrics.anaerobic_training_stress} label="Anaerobic Effect" />
                  </Grid>
                )}
              </Grid>

              {/* HR Comparisons */}
              {(zoneMetrics.max_hr_from_activity !== undefined || zoneMetrics.lactate_threshold_hr_from_activity !== undefined) && (
                <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Stack spacing={3}>
                    {/* Max HR Comparison */}
                    {zoneMetrics.max_hr !== undefined && zoneMetrics.max_hr_from_activity !== undefined && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Max Heart Rate Analysis</Typography>
                        {zoneMetrics.max_hr_from_activity > zoneMetrics.max_hr ? (
                          <Typography variant="body1" color="error.main">
                            This activity increased your detected Max HR by +{zoneMetrics.max_hr_from_activity - zoneMetrics.max_hr} bpm (+{(((zoneMetrics.max_hr_from_activity - zoneMetrics.max_hr) / zoneMetrics.max_hr) * 100).toFixed(1)}%).
                            New Max: {zoneMetrics.max_hr_from_activity} bpm.
                          </Typography>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            Activity Max HR ({zoneMetrics.max_hr_from_activity} bpm) was within your known max ({zoneMetrics.max_hr} bpm).
                          </Typography>
                        )}
                      </Box>
                    )}

                    {zoneMetrics.max_hr !== undefined && zoneMetrics.max_hr_from_activity !== undefined && zoneMetrics.lactate_threshold_hr !== undefined && <Divider />}

                    {/* LTHR Comparison */}
                    {zoneMetrics.lactate_threshold_hr !== undefined && zoneMetrics.lactate_threshold_hr_from_activity !== undefined && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Lactate Threshold Analysis</Typography>
                        {zoneMetrics.lactate_threshold_hr_from_activity > zoneMetrics.lactate_threshold_hr ? (
                          <Typography variant="body1" color="info.main">
                            Training detected +{zoneMetrics.lactate_threshold_hr_from_activity - zoneMetrics.lactate_threshold_hr} bpm ({(((zoneMetrics.lactate_threshold_hr_from_activity - zoneMetrics.lactate_threshold_hr) / zoneMetrics.lactate_threshold_hr) * 100).toFixed(1)}%) above your set LTHR ({zoneMetrics.lactate_threshold_hr} bpm → {zoneMetrics.lactate_threshold_hr_from_activity} bpm).
                          </Typography>
                        ) : (
                          <Typography variant="body1" color="text.secondary">
                            Maintained {((zoneMetrics.lactate_threshold_hr_from_activity / zoneMetrics.lactate_threshold_hr) * 100).toFixed(1)}% of your Lactate Threshold HR ({zoneMetrics.lactate_threshold_hr} bpm).
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ZoneDistributionChart;

